import { createMicroStore } from './micro-store.js';
import { DependencyTracker } from "./observables/observable-state.js";

const interactionContextSchema = {
    active: { owner: 'window' },
    focused: { owner: 'window' },
    hovered: { owner: 'window' },
    clicked: { owner: null, x: null, y: null },
    dragging: {
        status: 'idle',
        owner: 'window',
        startX: null,
        startY: null,
        currentX: null,
        currentY: null,
        sourceId: null,
        targetId: null,
        initialOffsetX: null,
        initialOffsetY: null
    },
    resizing: {
        status: 'idle',
        owner: 'window',
        startX: null,
        startY: null,
        currentX: null,
        currentY: null,
        initialWidth: null,
        initialHeight: null
    },
    keyPressed: { key: null, owner: 'window' },
    keyPressBuffer: { keys: [], owner: 'window' },
    lastKeyPressTime: { time: null, owner: 'window' },
    input: { 
        value: '', 
        owner: 'window',
        type: null,
        name: null
    },
    selection: { start: null, end: null, owner: 'window' },
    viewportSize: { width: 0, height: 0 },
    changed: { 
        owner: null, 
        type: null, 
        value: null, 
        previousValue: null,
        name: null
    },
    lastChanged: null,
    event: null
};

const interactionContextFromWindow = () => ({
    ...interactionContextSchema,
    viewportSize: { width: window.innerWidth, height: window.innerHeight }
});

const createInteractionReducer = (initialState) => (state = initialState, action) => {
    // Always include the event in the new state, even if it's null
    const newState = { ...state, event: action.event };
    let updatedState;

    switch (action.type) {
        case 'SET_FOCUSED':
            return { ...newState, focused: { owner: action.payload || 'window' } };
        case 'SET_HOVERED':
            return { ...newState, hovered: { owner: action.payload || 'window' } };
        case 'SET_ACTIVE':
            return { ...newState, active: { owner: action.payload || 'window' } };
        case 'SET_CLICKED':
            return { ...newState, clicked: action.payload };
        case 'CLEAR_CLICKED':
            return { ...newState, clicked: { owner: null, x: null, y: null } };
        case 'SET_DRAGGING':
            return { ...newState, dragging: { ...action.payload, owner: action.payload.owner || 'window' } };
        case 'SET_RESIZING':
            return { ...newState, resizing: { ...action.payload, status: action.payload.status || null } };
        case 'SET_KEY_PRESSED':
            return { ...newState, keyPressed: { ...action.payload, owner: action.payload.owner || 'window' } };
        case 'SET_KEY_PRESS_BUFFER':
            return { ...newState, keyPressBuffer: { keys: action.payload.keys, owner: action.payload.owner || 'window' } };
        case 'SET_LAST_KEY_PRESS_TIME':
            return { ...newState, lastKeyPressTime: { time: action.payload, owner: action.payload.owner || 'window' } };
        case 'SET_VIEWPORT_SIZE':
            return { ...newState, viewportSize: action.payload };
        case 'SET_INPUT':
        case 'SET_INPUT_RANGE':
            return {
                ...newState,
                input: {
                    value: action.payload.value ?? '',
                    owner: action.payload.owner ?? 'window',
                    type: action.payload.type ?? 'text',
                    name: action.payload.name ?? null
                }
            };
        case 'SET_SELECTION':
            return { ...newState, selection: { ...action.payload, owner: action.payload.owner || 'window' } };
        case 'SET_SELECT_CHANGED':
        case 'SET_MULTI_SELECT_CHANGED':
        case 'SET_CHECKBOX_CHANGED':
        case 'SET_RADIO_CHANGED':
        case 'SET_FILE_CHANGED':
        case 'SET_ELEMENT_CHANGED':
            updatedState = {
                ...newState,
                changed: {
                    owner: action.payload.owner ?? 'window',
                    type: action.payload.type ?? 'text',
                    value: action.payload.value ?? null,
                    previousValue: action.payload.previousValue ?? null,
                    name: action.payload.name ?? null
                },
                lastChanged: action.payload.name ?? null
            };
            break;
        case 'CLEAR_CHANGED':
            updatedState = {
                ...newState,  // This preserves the event
                changed: {
                    owner: null,
                    type: null,
                    value: null,
                    previousValue: null,
                    name: null
                },
                lastChanged: null
            };
            break;
        default:
            return newState;
    }

    return updatedState || newState;
};

const interactionReducer = createInteractionReducer(interactionContextFromWindow());
export const interactionStore = createMicroStore(interactionContextFromWindow(), interactionReducer);

let globalListenersInitialized = false;

function initializeInteractionStore() {
    if (globalListenersInitialized) return;

    const globalEvents = eventsFromDeviceType(deviceTypeFromUserAgent(navigator.userAgent));
    globalEvents.forEach(eventType => {
        if (['resize'].includes(eventType)) {
            window.addEventListener(eventType, dispatchGlobalInteraction);
        } else {
            document.addEventListener(eventType, dispatchGlobalInteraction);
        }
    });

    globalListenersInitialized = true;
}

// Initialize the interaction store immediately
initializeInteractionStore();

export function deviceTypeFromUserAgent(userAgent) {
    if (/Tablet|iPad/i.test(userAgent)) return 'tablet';
    if (/IEMobile|Windows Phone|Android|webOS|iPhone|iPod|BlackBerry|Opera Mini/i.test(userAgent)) return 'mobile';
    return 'desktop';
}

function eventsFromDeviceType(deviceType) {
    const commonEvents = [
        'focus', 'blur', 'focusin', 'focusout',
        'keydown', 'keyup',
        'mousedown', 'mouseup', 'mousemove', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave',
        'touchstart', 'touchend', 'touchmove', 'touchcancel',
        'resize', 'orientationchange',
        'dragstart', 'drag', 'dragend', 'dragenter', 'dragover', 'dragleave', 'drop',
        'scroll', 'wheel',
        'click',
        'input', 'change', 'cut', 'copy', 'paste', 'select'
    ];

    const mobileEvents = [
        'devicemotion', 'deviceorientation'
    ];

    return (deviceType === 'mobile' || deviceType === 'tablet' || isTouchDevice())
        ? [...commonEvents, ...mobileEvents]
        : commonEvents;
}

function isTouchDevice() {
    return (('ontouchstart' in window) ||
       (navigator.maxTouchPoints > 0) ||
       (navigator.msMaxTouchPoints > 0));
}

const touchToMouseMap = {
    'touchstart': 'mousedown',
    'touchend': 'mouseup',
    'touchmove': 'mousemove',
    'touchcancel': 'mouseout'
};

function dispatchGlobalInteraction(event) {
    const closestNodeElement = event.target instanceof Element ? event.target.closest('[node-id]') : null;
    const targetId = closestNodeElement ? closestNodeElement.getAttribute('node-id') : 'window';
    
    const eventType = touchToMouseMap[event.type] || event.type;
    
    const actions = actionFromEventType(eventType, interactionStore)(event);
    if (actions) {
        if (Array.isArray(actions)) {
            actions.forEach(action => {
                interactionStore.dispatch(action);
            });
        } else {
            interactionStore.dispatch(actions);
        }
    }
}

function actionFromEventType(type, store) {
    return (event) => {
        const targetId = event.target instanceof Element ? event.target.closest('[node-id]')?.getAttribute('node-id') : 'window';
        const draggingState = store.getState().dragging;
        const resizingState = store.getState().resizing;

        const coordinatesFromEvent = (evt) => {
            return evt.touches ? { clientX: evt.touches[0].clientX, clientY: evt.touches[0].clientY } : evt;
        };

        const mappedType = touchToMouseMap[type] || type;

        switch (mappedType) {
            case 'focus':
                return { type: 'SET_FOCUSED', payload: targetId, event };
            case 'blur':
                return { type: 'SET_FOCUSED', payload: null, event: null };
            case 'mouseover':
                return { type: 'SET_HOVERED', payload: targetId, event };
            case 'mouseout':
                return { type: 'SET_HOVERED', payload: null, event: null };
            case 'click':
            case 'touchend':
                const { clientX, clientY } = coordinatesFromEvent(event);
                return [
                    { type: 'SET_CLICKED', payload: { owner: targetId, x: clientX, y: clientY, target: event.target }, event },
                    { type: 'CLEAR_CLICKED', payload: { owner: targetId, x: clientX, y: clientY }, event: null }
                ];
            case 'mousedown':
                if (targetId?.startsWith('resize-handle')) {
                    const resizeDirection = targetId.split('-').slice(2).join('-');
                    const targetElement = event.target.closest('.window');
                    const rect = targetElement.getBoundingClientRect();
                    return {
                        type: 'SET_RESIZING',
                        payload: {
                            status: `resizing-${resizeDirection}`,
                            owner: targetId,
                            initialWidth: rect.width,
                            initialHeight: rect.height,
                            startX: event.clientX,
                            startY: event.clientY,
                            currentX: event.clientX,
                            currentY: event.clientY
                        },
                        event
                    };
                }
                return [
                    { 
                        type: 'SET_DRAGGING', 
                        payload: { 
                            status: 'dragging', 
                            owner: targetId,
                            startX: event.clientX,
                            startY: event.clientY,
                            currentX: event.clientX,
                            currentY: event.clientY,
                            initialOffsetX: event.target.getBoundingClientRect().left,
                            initialOffsetY: event.target.getBoundingClientRect().top
                        },
                        event
                    },
                    {
                        type: 'SET_ACTIVE',
                        payload: targetId,
                        event
                    },
                ];
            case 'mouseup':
                const actions = [];
                if (resizingState.status.startsWith('resizing')) {
                    actions.push({ type: 'SET_RESIZING', payload: { ...resizingState, status: 'idle' }, event: null });
                }
                if (draggingState.status === 'dragging') {
                    actions.push({ type: 'SET_DRAGGING', payload: { ...draggingState, status: 'idle' }, event: null });
                }
                actions.push({ 
                    type: 'SET_ACTIVE', 
                    payload: null,
                    event: null
                })
                return actions;
            case 'mousemove':
                const { clientX: moveX, clientY: moveY } = coordinatesFromEvent(event);
                const moveActions = [];
                if (draggingState.status === 'dragging') {
                    moveActions.push({ type: 'SET_DRAGGING', payload: { ...draggingState, currentX: moveX, currentY: moveY }, event });
                }
                if (resizingState.status.startsWith('resizing')) {
                    moveActions.push({ type: 'SET_RESIZING', payload: { ...resizingState, currentX: moveX, currentY: moveY }, event });
                }
                return moveActions.length > 0 ? moveActions : null;
            case 'keydown':
            case 'keyup':
                const keyActions = keyPressBufferFromEvent(store, event, targetId);
                return keyActions.map(action => ({ ...action, event }));
            case 'resize':
            case 'orientationchange':
                return { type: 'SET_VIEWPORT_SIZE', payload: { width: window.innerWidth, height: window.innerHeight }, event };
            case 'change':
                const changeAction = handleChangeEvent(event, store, targetId);
                return [
                    changeAction,
                    { type: 'CLEAR_CHANGED', event: event }  // Include the original event
                ];
            case 'input':
                const inputAction = handleInputEvent(event, store, targetId);
                return [
                    inputAction,
                    { type: 'CLEAR_CHANGED', event: event }  // Include the original event
                ];
            case 'select':
                const { selectionStart, selectionEnd } = event.target;
                return { type: 'SET_SELECTION', payload: { start: selectionStart, end: selectionEnd, owner: targetId }, event };
            case 'cut':
            case 'copy':
            case 'paste':
                return { 
                    type: 'SET_ELEMENT_CHANGED', 
                    payload: { 
                        owner: targetId, 
                        type: event.type,
                        value: event.target.value,
                        previousValue: event.target.defaultValue,
                        name: event.target.name || null
                    }, 
                    event 
                };
            default:
                return {
                    type: 'DEFAULT_PAYLOAD',
                    payload: {
                        owner: targetId,
                        type: mappedType,
                        value: null,
                        name: event.target?.name || null
                    },
                    event
                };
        }
    };
}

function getInputType(element) {
    if (element.type) return element.type;
    if (element.tagName.toLowerCase() === 'textarea') return 'textarea';
    if (element.tagName.toLowerCase() === 'select') {
        return element.multiple ? 'select-multiple' : 'select-one';
    }
    return 'text';
}

function handleInputEvent(event, store, targetId) {
    const elementType = getInputType(event.target);
    let value, previousValue;

    switch (elementType) {
        case 'text':
        case 'textarea':
        case 'password':
        case 'number':
        case 'email':
        case 'tel':
        case 'url':
        case 'search':
        case 'date':
        case 'time':
        case 'datetime-local':
        case 'month':
        case 'week':
        case 'color':
            value = event.target.value;
            previousValue = store.getState().input.value;
            return {
                type: 'SET_INPUT',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        case 'range':
            value = parseFloat(event.target.value);
            previousValue = parseFloat(store.getState().input.value);
            return {
                type: 'SET_INPUT_RANGE',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        default:
            return null;
    }
}

function handleChangeEvent(event, store, targetId) {
    const elementType = getInputType(event.target);
    let value, previousValue;

    switch (elementType) {
        case 'select-one':
            value = event.target.value;
            previousValue = store.getState().changed.value;
            return {
                type: 'SET_SELECT_CHANGED',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        case 'select-multiple':
            value = Array.from(event.target.selectedOptions).map(option => option.value);
            previousValue = store.getState().changed.value || [];
            return {
                type: 'SET_MULTI_SELECT_CHANGED',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        case 'checkbox':
            value = event.target.checked;
            previousValue = store.getState().changed.type === 'checkbox' ? store.getState().changed.value : null;
            return {
                type: 'SET_CHECKBOX_CHANGED',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        case 'radio':
            value = event.target.value;
            previousValue = store.getState().changed.value;
            return {
                type: 'SET_RADIO_CHANGED',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        case 'file':
            value = Array.from(event.target.files);
            previousValue = store.getState().changed.value || [];
            return {
                type: 'SET_FILE_CHANGED',
                payload: {
                    owner: targetId,
                    type: elementType,
                    value: value,
                    previousValue: previousValue,
                    name: event.target.name || null
                },
                event
            };
        default:
            return handleInputEvent(event, store, targetId);
    }
}

function keyPressBufferFromEvent(store, event, targetId) {
    const { keyPressBuffer } = store.getState();
    let newBuffer = new Set(Array.isArray(keyPressBuffer.keys) ? keyPressBuffer.keys : []);

    if (event.type === 'keydown') {
        newBuffer.add(event.key);
    } else if (event.type === 'keyup') {
        newBuffer.delete(event.key);
        newBuffer.delete('Meta');
    }

    const sortedBuffer = Array.from(newBuffer).sort().join('+');

    return [
        { type: 'SET_KEY_PRESS_BUFFER', payload: { keys: Array.from(newBuffer), owner: targetId }, event },
        { type: 'SET_KEY_PRESSED', payload: { key: sortedBuffer, owner: targetId }, event }
    ];
}
